import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import {
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  ResetButton,
} from '../VariationOne';
import { Filler } from '../shared';
import CoverBox from './CoverBox';
import GeneratedImages from './GeneratedImages';
import VariationsStyles from './VariationStyles';

export interface ImageVariation {
  coverText: string;
  coverColor: string;
  authorName: string;
  channelNameColor: string;
  authorImage?: string;
  fontSize?: number;
  channelImageOption: string;
}

export interface ImageVariationParams {
  coverText: string;
  coverColor: string;
  authorName: string;
  channelNameColor: string;
  authorImages: ImageData[];
  fontSize?: string;
  channelImageOption: string;
}

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 24px;
`;

const ButtonsHolder = styled.div`
  padding: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

interface Props {
  inArticle?: boolean;
}

const VariationFour: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();

  const [coverParameters, setCoverParameters] = useState<
    ImageVariationParams[]
  >([
    {
      coverText: '',
      coverColor: '#FFB054',
      authorName: '',
      channelNameColor: 'black',
      authorImages: [],
      channelImageOption: 'reddit',
    },
  ]);

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = coverParameters.map((coverParams) => ({
      coverText: coverParams.coverText,
      fontSize: coverParams.fontSize ? parseInt(coverParams.fontSize) : 0,
      authorName: coverParams.authorName,
      authorImage: !!coverParams.authorImages.length
        ? coverParams.authorImages[0]?.croppedImage ??
          URL.createObjectURL(coverParams.authorImages[0].image)
        : undefined,
      coverColor: coverParams.coverColor,
      channelNameColor: coverParams.channelNameColor,
      channelImageOption: coverParams.channelImageOption,
    }));

    setVariations(newVariations);
  };

  const onAddMoreCovers = () => {
    setCoverParameters((prev) => [
      ...prev,
      {
        coverText: '',
        coverColor: '#FFB054',
        authorName: '',
        channelNameColor: 'black',
        authorImages: [],
        channelImageOption: 'reddit',
      },
    ]);
  };

  const onReset = () => {
    setCoverParameters([
      {
        coverText: '',
        coverColor: '#FFB054',
        authorName: '',
        channelNameColor: 'black',
        authorImages: [],
        channelImageOption: 'reddit',
      },
    ]);
    setImages([]);
    setVariations([]);
  };

  return (
    <>
      <Box>
        <Filler fontFamily="Arial" />
        <Header>
          <div>
            <Heading $type="button">Simple Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Add text and channel name alongside the channel image, choose a
              cover border color, and include the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton color="red" onClick={onReset}>
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            {coverParameters.map((cover, index) => (
              <CoverBox
                key={index}
                cover={cover}
                setCoverParameters={setCoverParameters}
                coverIndex={index}
              />
            ))}
            <ButtonsHolder>
              <Button color="colorized" onClick={onAddMoreCovers}>
                Add More Covers
              </Button>
            </ButtonsHolder>
          </>
        )}
      </Box>
      <VariationsStyles />
      {showContent && (
        <ButtonHolder>
          <Button onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
          <Button color="reset" onClick={onReset}>
            Reset
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
    </>
  );
};

export default VariationFour;
